import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import useDebounce from '../../hooks/useDebounce';
import { Calendar, ChevronLeft, ChevronRight, Eye, EyeOff, Edit2, Trash2, ChevronDown, X } from 'lucide-react';
import Dropdown from '../../components/Dropdown';
import DateFilter from '../../components/DateFilter';
import { isDateInRange, formatAppDate, formatAppDateOnly } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import PrinterLoader from '../../components/PrinterLoader';
import { BASE_URL } from '../../utils/api';

const UserManagement = () => {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  
  const [rolesList, setRolesList] = useState([]);
  const [permissions, setPermissions] = useState({ flat: [], grouped: {} });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { default: api } = await import('../../utils/api');
      
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage
      });
      
      if (debouncedSearchTerm) queryParams.append('search', debouncedSearchTerm);
      if (roleFilter !== 'all') queryParams.append('role', roleFilter);
      // Note: Date filter is kept simple for now, can be expanded to backend if needed

      const res = await api.get(`/auth/users?${queryParams.toString()}`);
      
      if (res.data && res.data.data) {
        setUsers(res.data.data.map(u => ({
          id: u.id,
          name: u.name,
          email: u.email,
          phone: u.phone,
          address: u.address,
          role: u.role,
          status: u.status || 'Active',
          joinedAt: u.createdAt,
          themeColor: u.themeColor || '#3b82f6',
          features: u.features || [],
          firstName: u.firstName,
          lastName: u.lastName,
          profileImage: u.profileImage,
          address1: u.address1,
          address2: u.address2,
          city: u.city,
          state: u.state,
          pincode: u.pincode,
          aadharCard: u.aadharCard,
          panCard: u.panCard,
          aadharCard: u.aadharCard,
          panCard: u.panCard,
          firstName: u.firstName,
          lastName: u.lastName,
          profileImage: u.profileImage,
          address1: u.address1,
          address2: u.address2,
          city: u.city,
          state: u.state,
          pincode: u.pincode
        })));
        setTotalPages(res.data.totalPages || 1);
        setTotalUsers(res.data.total || 0);
      } else if (res.data && Array.isArray(res.data)) {
        // Fallback if backwards compatibility kicks in
        setUsers(res.data.map(u => ({
          id: u.id,
          name: u.name,
          email: u.email,
          phone: u.phone,
          address: u.address,
          role: u.role,
          status: u.status || 'Active',
          joinedAt: u.createdAt,
          themeColor: u.themeColor || '#3b82f6',
          features: u.features || []
        })));
        setTotalPages(1);
        setTotalUsers(res.data.length);
      }
    } catch (err) {
      console.error('Failed to fetch users', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, itemsPerPage, debouncedSearchTerm, roleFilter]);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const { default: api } = await import('../../utils/api');
        const [rolesRes, permsRes] = await Promise.all([
          api.get('/roles'),
          api.get('/permissions')
        ]);
        setRolesList(rolesRes.data);
        setPermissions(permsRes.data);
      } catch (err) {
        console.error('Failed to fetch roles/permissions', err);
      }
    };
    fetchMetadata();
  }, []);

  const [showModal, setShowModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', role: 'Telecaller', status: 'Active', themeColor: '#3b82f6', features: ['leads'], city: 'Surat', state: 'Gujarat' });

  const confirmDelete = (id) => {
    setUserToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (userToDelete) {
      try {
        const { default: api } = await import('../../utils/api');
        await api.delete(`/auth/users/${userToDelete}`);
        setUsers(users.filter(u => u.id !== userToDelete));
        toast.success('User deleted successfully');
        setDeleteModalOpen(false);
        setUserToDelete(null);
      } catch (error) {
        console.error('Failed to delete user', error);
        toast.error(error.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const handleOpenModal = (user = null, viewMode = false) => {
    setIsViewMode(viewMode);
    if (user) {
      setEditId(user.id);
      const features = user.features || [];
      
      let fName = user.firstName || '';
      let lName = user.lastName || '';
      
      // Auto-split name for legacy users who don't have firstName/lastName in DB
      if (!fName && !lName && user.name) {
        const parts = user.name.trim().split(' ');
        fName = parts[0] || '';
        lName = parts.slice(1).join(' ') || '';
      }
      
      setFormData({ name: user.name, email: user.email, phone: user.phone || '', address: user.address || '', role: user.role, status: user.status, themeColor: user.themeColor || '#3b82f6', features, password: '', firstName: fName, lastName: lName, profileImage: user.profileImage || '', address1: user.address1 || '', address2: user.address2 || '', city: user.city || '', state: user.state || '', pincode: user.pincode || '', aadharCard: user.aadharCard || '', panCard: user.panCard || '' });
    } else {
      setEditId(null);
      setFormData({ name: '', email: '', phone: '', address: '', role: 'Telecaller', status: 'Active', themeColor: '#3b82f6', features: [], password: '', firstName: '', lastName: '', profileImage: '', address1: '', address2: '', city: 'Surat', state: 'Gujarat', pincode: '', aadharCard: '', panCard: '' });
    }
    setShowPassword(false);
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isViewMode) {
      setShowModal(false);
      return;
    }
    
    const payload = { ...formData };
    payload.name = `${payload.firstName || ''} ${payload.lastName || ''}`.trim();
    
    setIsSubmitting(true);
    try {
      const { default: api } = await import('../../utils/api');
      if (editId) {
        await api.put(`/auth/users/${editId}`, payload);
        toast.success('User updated successfully');
      } else {
        await api.post('/auth/users', payload);
        toast.success('User created successfully');
      }
      await fetchUsers();
      setShowModal(false);
    } catch (error) {
      console.error('Failed to save user', error);
      toast.error(error.response?.data?.message || 'Failed to save user');
    }
  };

  const toggleFeature = (feature) => {
    const current = formData.features || [];
    if (current.includes(feature)) {
      setFormData({ ...formData, features: current.filter(f => f !== feature) });
    } else {
      setFormData({ ...formData, features: [...current, feature] });
    }
  };

  const paginatedUsers = users.filter(user => {
    // Only keep local date filtering since it wasn't added to backend yet
    const matchesDate = true;
    return matchesDate;
  });

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div>
      {loading && <PrinterLoader />}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">User Management</h2>
        {hasPermission('users:write') && (
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>Add New User</button>
        )}
      </div>

      <div className="card">
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex justify-between items-center gap-4 flex-wrap">
             <input 
               type="text" 
               placeholder="Search users..." 
               className="input-field" 
               style={{ flex: '1', minWidth: '250px', maxWidth: '320px' }}
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)} 
             />
             <div className="flex gap-4 items-center flex-wrap">
               <div className="flex items-center gap-2">
                 <Dropdown
                   value={roleFilter}
                   onChange={(val) => { setRoleFilter(val); setCurrentPage(1); }}
                   options={[
                     { value: 'all', label: 'All Roles' },
                     { value: 'Admin', label: 'Admin' },
                     { value: 'Marketing User', label: 'Marketing User' },
                     { value: 'Telecaller', label: 'Telecaller' }
                   ]}
                 />
               </div>
               <button className="btn" style={{ border: '1.5px solid var(--color-border)', backgroundColor: 'white' }}>Export</button>
             </div>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-muted)' }}>
                <th style={{ padding: '0.75rem', width: '70px' }}>Sr. No.</th>
                <th style={{ padding: '0.75rem' }}>Name</th>
                <th style={{ padding: '0.75rem' }}>Email</th>
                <th style={{ padding: '0.75rem' }}>Joined Date</th>
                <th style={{ padding: '0.75rem' }}>Role</th>
                <th style={{ padding: '0.75rem' }}>Status</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.length > 0 ? paginatedUsers.map((user, index) => (
                <tr key={user.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.75rem', color: 'var(--color-text-muted)' }}>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td style={{ padding: '0.75rem', fontWeight: '500' }}>
                    {user.name}
                  </td>
                  <td style={{ padding: '0.75rem' }}>{user.email}</td>
                  <td style={{ padding: '0.75rem', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                    {formatAppDate(user.joinedAt)}
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    <span style={{ 
                      padding: '0.25rem 0.6rem', 
                      borderRadius: '9999px', 
                      backgroundColor: 
                        user.role === 'Admin' ? '#fecaca' : 
                        (user.role === 'Marketing User' || user.role === 'Marketing Agent') ? '#e0e7ff' : 
                        user.role === 'Telecaller' ? '#ffedd5' :
                        user.role === 'Delivery Boy' ? '#dcfce7' : '#f1f5f9',
                      color: 
                        user.role === 'Admin' ? '#991b1b' : 
                        (user.role === 'Marketing User' || user.role === 'Marketing Agent') ? '#3730a3' : 
                        user.role === 'Telecaller' ? '#c2410c' :
                        user.role === 'Delivery Boy' ? '#166534' : '#475569',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {user.role}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    <span style={{ 
                      padding: '0.25rem 0.6rem', 
                      borderRadius: '9999px', 
                      backgroundColor: user.status === 'Active' ? '#dcfce7' : '#f1f5f9',
                      color: user.status === 'Active' ? '#166534' : '#475569',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {user.status}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <div className="flex gap-2 justify-center">
                      <button 
                        className="btn text-primary" 
                        onClick={() => handleOpenModal(user, true)} 
                        title="View User" 
                        style={{ padding: '0.3rem' }}
                      >
                        <Eye size={18} />
                      </button>
                      {hasPermission('users:write') && (
                        <button 
                          className="btn text-muted" 
                          onClick={() => handleOpenModal(user)} 
                          title="Edit User" 
                          style={{ padding: '0.3rem' }}
                        >
                          <Edit2 size={18} />
                        </button>
                      )}
                      {user.role !== 'Admin' && hasPermission('users:delete') && (
                        <button 
                          className="btn text-danger" 
                          onClick={() => confirmDelete(user.id)} 
                          title="Delete User" 
                          style={{ padding: '0.3rem' }}
                        >
                          <Trash2 size={18} color="var(--color-danger)" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                    No users found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalUsers > 0 && (
          <div className="flex justify-between items-center mt-6 fade-in" style={{ padding: '0 0.5rem' }}>
            <div className="flex items-center gap-4 text-sm text-muted">
              <div>
                Showing <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{Math.min(currentPage * itemsPerPage, totalUsers)}</span> of <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{totalUsers}</span> entries
              </div>
              <div className="flex items-center gap-2">
                <span>Show:</span>
                <Dropdown 
                  style={{ width: '80px', height: '32px' }}
                  value={itemsPerPage}
                  onChange={(val) => {
                    setItemsPerPage(Number(val));
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                    { value: 50, label: '50' }
                  ]}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                className="btn" 
                style={{ padding: '0.5rem', backgroundColor: 'white', border: '1px solid var(--color-border)', color: currentPage === 1 ? 'var(--color-text-muted)' : 'var(--color-text-main)', opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button 
                  key={page}
                  className={`btn ${currentPage === page ? 'btn-primary' : ''}`}
                  style={{ 
                    padding: '0.5rem 1rem', 
                    backgroundColor: currentPage === page ? 'var(--color-primary)' : 'white', 
                    border: currentPage === page ? 'none' : '1px solid var(--color-border)',
                    color: currentPage === page ? 'white' : 'var(--color-text-main)'
                  }}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}
              <button 
                className="btn" 
                style={{ padding: '0.5rem', backgroundColor: 'white', border: '1px solid var(--color-border)', color: currentPage === totalPages || totalPages === 0 ? 'var(--color-text-muted)' : 'var(--color-text-main)', opacity: currentPage === totalPages || totalPages === 0 ? 0.5 : 1, cursor: currentPage === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer' }}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit User Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 100,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'var(--color-bg-card)',
            padding: '2rem',
            borderRadius: 'var(--border-radius-lg)',
            width: '950px',
            maxWidth: '95%',
            boxShadow: 'var(--shadow-xl)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">{isViewMode ? 'View User Details' : editId ? 'Edit User' : 'Add New User'}</h3>
              <button type="button" onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSave} className="flex flex-col gap-5">
              <div className="flex flex-col gap-6">
                
                {/* Personal Information */}
                <div>
                  <h4 className="font-bold mb-4 uppercase tracking-wider" style={{ fontSize: '0.75rem', color: 'var(--color-primary)' }}>Personal Information</h4>
                  
                  <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem' }}>
                    <div style={{ width: '100px', height: '100px', borderRadius: '50%', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '2px dashed var(--color-border)', flexShrink: 0, position: 'relative', cursor: !isViewMode ? 'pointer' : 'default' }} onClick={() => { if(!isViewMode) document.getElementById('profileImageInput').click(); }}>
                       {formData.profileImage ? (
                         <img src={formData.profileImage.startsWith('data:') ? formData.profileImage : (formData.profileImage.startsWith('/') ? `${BASE_URL}${formData.profileImage}` : formData.profileImage)} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                       ) : (
                         <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Upload Photo</span>
                       )}
                       {!isViewMode && (
                         <input type="file" id="profileImageInput" accept="image/*" style={{ display: 'none' }} onChange={(e) => {
                           if(e.target.files && e.target.files[0]) {
                             const reader = new FileReader();
                             reader.onload = () => setFormData({...formData, profileImage: reader.result});
                             reader.readAsDataURL(e.target.files[0]);
                           }
                         }} />
                       )}
                    </div>
                    
                    <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-muted">First Name {!isViewMode && <span className="text-danger">*</span>}</label>
                        {isViewMode ? (
                          <div className="text-slate-800 font-medium text-base">{formData.firstName}</div>
                        ) : (
                          <input type="text" className="input-field w-full" required value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} placeholder="e.g. John" />
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-muted">Last Name {!isViewMode && <span className="text-danger">*</span>}</label>
                        {isViewMode ? (
                          <div className="text-slate-800 font-medium text-base">{formData.lastName}</div>
                        ) : (
                          <input type="text" className="input-field w-full" required value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} placeholder="e.g. Doe" />
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-muted">Email Address {!isViewMode && <span className="text-danger">*</span>}</label>
                        {isViewMode ? (
                          <div className="text-slate-800 font-medium text-base">{formData.email}</div>
                        ) : (
                          <input type="email" className="input-field w-full" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="john@example.com" />
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-muted">Phone Number</label>
                        {isViewMode ? (
                          <div className="text-slate-800 font-medium text-base">{formData.phone || 'N/A'}</div>
                        ) : (
                          <input type="tel" className="input-field w-full" value={formData.phone} onChange={e => {
                            const val = e.target.value.replace(/\D/g, '');
                            if (val.length <= 10) {
                              setFormData({...formData, phone: val});
                            }
                          }} maxLength="10" minLength="10" pattern="[0-9]{10}" placeholder="+91 98765 43210" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-muted">Aadhar Card</label>
                      {isViewMode ? <div className="text-slate-800 font-medium text-base">{formData.aadharCard || 'N/A'}</div> : <input type="text" className="input-field w-full" value={formData.aadharCard} onChange={e => setFormData({...formData, aadharCard: e.target.value})} placeholder="Aadhar Number" />}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-muted">PAN Card</label>
                      {isViewMode ? <div className="text-slate-800 font-medium text-base">{formData.panCard || 'N/A'}</div> : <input type="text" className="input-field w-full" value={formData.panCard} onChange={e => setFormData({...formData, panCard: e.target.value})} placeholder="PAN Number" />}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-muted">Address Line 1</label>
                      {isViewMode ? <div className="text-slate-800 font-medium text-base">{formData.address1 || 'N/A'}</div> : <textarea className="input-field w-full" rows="2" style={{ resize: 'none' }} value={formData.address1} onChange={e => setFormData({...formData, address1: e.target.value})} placeholder="Street address, building, etc." />}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-muted">Address Line 2</label>
                      {isViewMode ? <div className="text-slate-800 font-medium text-base">{formData.address2 || 'N/A'}</div> : <textarea className="input-field w-full" rows="2" style={{ resize: 'none' }} value={formData.address2} onChange={e => setFormData({...formData, address2: e.target.value})} placeholder="Apartment, suite, unit, etc. (optional)" />}
                    </div>
                    <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-muted">City</label>
                        {isViewMode ? <div className="text-slate-800 font-medium text-base">{formData.city || 'N/A'}</div> : <input type="text" className="input-field w-full" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} placeholder="City" />}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-muted">State</label>
                        {isViewMode ? <div className="text-slate-800 font-medium text-base">{formData.state || 'N/A'}</div> : <input type="text" className="input-field w-full" value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} placeholder="State" />}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-muted">Pincode</label>
                        {isViewMode ? <div className="text-slate-800 font-medium text-base">{formData.pincode || 'N/A'}</div> : <input type="text" className="input-field w-full" value={formData.pincode} onChange={e => setFormData({...formData, pincode: e.target.value})} placeholder="Pincode" />}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Account Settings */}
                <div className="pt-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
                  <h4 className="font-bold mb-4 mt-2 uppercase tracking-wider" style={{ fontSize: '0.75rem', color: 'var(--color-primary)' }}>Account Settings</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-muted">Role</label>
                      {isViewMode ? (
                        <div className="text-slate-800 font-medium text-base">{formData.role}</div>
                      ) : (
                        <Dropdown 
                          value={formData.role} 
                          onChange={val => {
                            // Find the selected role from rolesList
                            const selectedRole = rolesList.find(r => r.name === val);
                            const rolePerms = selectedRole ? selectedRole.permissions.map(p => p.name) : [];
                            setFormData({...formData, role: val, features: rolePerms});
                          }} 
                          options={rolesList.map(r => ({ value: r.name, label: r.name }))} 
                        />
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-muted">Status</label>
                      {isViewMode ? (
                        <div className="text-slate-800 font-medium text-base flex items-center gap-2">
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: formData.status === 'Active' ? '#10b981' : '#94a3b8' }}></span>
                          {formData.status}
                        </div>
                      ) : (
                        <Dropdown 
                          value={formData.status} 
                          onChange={val => setFormData({...formData, status: val})} 
                          options={[
                            { value: 'Active', label: 'Active', render: () => (<div className="flex items-center gap-2"><span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981' }}></span>Active</div>) },
                            { value: 'Inactive', label: 'Inactive', render: () => (<div className="flex items-center gap-2"><span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#94a3b8' }}></span>Inactive</div>) }
                          ]} 
                        />
                      )}
                    </div>


                    {!isViewMode && (
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-muted">Password {!editId && <span className="text-danger">*</span>}</label>
                        <div style={{ position: 'relative' }}>
                          <input 
                            type={showPassword ? "text" : "password"} 
                            className="input-field w-full" 
                            required={!editId} 
                            value={formData.password || ''} 
                            onChange={e => setFormData({...formData, password: e.target.value})} 
                            placeholder={editId ? "Leave blank to keep current password" : "Enter a secure password"} 
                            style={{ paddingRight: '2.5rem' }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              position: 'absolute',
                              right: '0.75rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              color: 'var(--color-text-muted)',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '0.25rem'
                            }}
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Feature Access & Permissions */}
                <div className="pt-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
                  <div 
                    className="flex justify-between items-center cursor-pointer select-none mt-2" 
                    onClick={() => setIsPermissionsOpen(!isPermissionsOpen)}
                  >
                    <h4 className="font-bold uppercase tracking-wider m-0" style={{ fontSize: '0.75rem', color: 'var(--color-primary)' }}>
                      Permissions & Access
                    </h4>
                    {isPermissionsOpen ? <ChevronDown size={18} color="var(--color-primary)" /> : <ChevronRight size={18} color="var(--color-primary)" />}
                  </div>
                  
                  {isPermissionsOpen && (
                    <div className="mt-4">
                  <div style={{ display: 'grid', gap: '20px', backgroundColor: '#f9fafb', padding: '20px', borderRadius: '8px', border: '1px solid #f3f4f6' }}>
                      {Object.keys(permissions.grouped || {}).sort((a, b) => {
                        const moduleOrder = ['dashboard', 'leads', 'customers', 'followups', 'users', 'roles', 'settings', 'reports', 'masters'];
                        const indexA = moduleOrder.indexOf(a.toLowerCase());
                        const indexB = moduleOrder.indexOf(b.toLowerCase());
                        if (indexA === -1 && indexB === -1) return a.localeCompare(b);
                        if (indexA === -1) return 1;
                        if (indexB === -1) return -1;
                        return indexA - indexB;
                      }).map(moduleName => {
                        const sortedPerms = [...permissions.grouped[moduleName]].sort((a, b) => {
                          const getAction = (perm) => {
                            const str = (perm.name + ' ' + (perm.description || '')).toLowerCase();
                            if (str.includes('delete')) return 5;
                            if (str.includes('manage') || str.includes('update')) return 4;
                            if (str.includes('write') || str.includes('create')) return 2;
                            if (str.includes('read') || str.includes('view')) return 1;
                            return 99;
                          };
                          const indexA = getAction(a);
                          const indexB = getAction(b);
                          if (indexA !== indexB) return indexA - indexB;
                          return a.name.localeCompare(b.name);
                        });
                        return (
                          <div key={moduleName}>
                            <h5 style={{ margin: '0 0 12px 0', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', color: '#4b5563', fontSize: '0.95rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{moduleName}</h5>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                              {sortedPerms.map(perm => (
                                <label key={perm.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: isViewMode ? 'default' : 'pointer', fontSize: '0.9rem', color: '#374151', padding: '4px 0', opacity: isViewMode ? 0.8 : 1 }}>
                                  <input 
                                    type="checkbox" 
                                    disabled={isViewMode}
                                    checked={formData.features?.includes(perm.name) || false} 
                                    onChange={() => toggleFeature(perm.name)} 
                                    style={{ marginTop: '3px', width: '16px', height: '16px', accentColor: 'var(--color-primary, #3b82f6)', cursor: isViewMode ? 'default' : 'pointer' }}
                                  />
                                  <span style={{ lineHeight: '1.4' }}>{perm.description || perm.name}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end mt-6 pt-4 border-t" style={{ borderColor: 'var(--color-border)', gap: '1rem' }}>
                <button type="button" className="btn" onClick={() => setShowModal(false)} style={{ border: '1px solid var(--color-border)', backgroundColor: 'white' }}>{isViewMode ? 'Close' : 'Cancel'}</button>
                {!isViewMode && <button type="submit" className="btn btn-primary" disabled={!formData.firstName || !formData.email}>Save User</button>}
              </div>
            </form>
          </div>
        </div>
      )}
      {deleteModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '1rem' }} onClick={() => setDeleteModalOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="slide-up" style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '400px', padding: '2rem', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', textAlign: 'center' }}>
            <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
              <Trash2 size={32} color="var(--color-danger)" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-main">Delete User</h3>
            <p className="text-muted mb-8 text-sm">Are you sure you want to delete this user? This action cannot be undone.</p>
            <div className="flex gap-4 justify-center">
              <button className="btn flex-1" onClick={() => setDeleteModalOpen(false)} style={{ border: '1px solid var(--color-border)', backgroundColor: 'white' }}>Cancel</button>
              <button className="btn btn-primary flex-1" onClick={handleDelete} style={{ backgroundColor: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {deleteModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '1rem' }} onClick={() => setDeleteModalOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="slide-up" style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '400px', padding: '2rem', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', textAlign: 'center' }}>
            <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
              <Trash2 size={32} color="var(--color-danger)" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-main">Delete User</h3>
            <p className="text-muted mb-8 text-sm">Are you sure you want to delete this user? This action cannot be undone.</p>
            <div className="flex gap-4 justify-center">
              <button className="btn flex-1" onClick={() => setDeleteModalOpen(false)} style={{ border: '1px solid var(--color-border)', backgroundColor: 'white' }}>Cancel</button>
              <button className="btn btn-primary flex-1" onClick={handleDelete} style={{ backgroundColor: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>Delete</button>
            </div>
          </div>
        </div>
      )}



      

      
    </div>
  );
};
;


export default UserManagement;
